import React, {useState, useEffect, useRef, FC} from 'react'
import './SegmentedInput.scss'

interface Props {
  value: string | null
  onChange: (e: any) => void
  name: string
}

const SegmentedInput: FC<Props> = ({value, onChange, name}) => {
  const lengths = [2, 2, 2, 2, 2, 2]
  const inputRefs = useRef<HTMLInputElement[]>([])

  const [segments, setSegments] = useState<string[]>(Array(6).fill(''))

  useEffect(() => {
    if (value) {
      const parts = value.split(' ')
      const newSegments = Array(6).fill('')
      parts.forEach((p, i) => {
        let segmentVal = p
        if (i === 0 && segmentVal === '00') {
          segmentVal = '01'
        }
        if (i < 6) newSegments[i] = segmentVal
      })
      setSegments(newSegments)
    }
  }, [value])

  const notifyChange = (newSegments: string[]) => {
    const joinedValue = newSegments.join(' ')
    const fakeEvent = {
      target: {
        name,
        value: joinedValue,
      },
    }
    onChange(fakeEvent)
  }

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^0-9]/g, '') // Only digits
    const maxLength = lengths[index]

    if (index === 0 && val === '00') {
      return
    }

    if (val.length <= maxLength) {
      const newSegments = [...segments]
      newSegments[index] = val
      setSegments(newSegments)
      notifyChange(newSegments)

      // Auto-focus next
      if (val.length === maxLength && index < 5) {
        inputRefs.current[index + 1]?.focus()
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !segments[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleBlur = (index: number) => {
    if (index === 0) {
      const val = segments[0]
      if (val === '0' || val === '00') {
        const newSegments = [...segments]
        newSegments[0] = '01'
        setSegments(newSegments)
        notifyChange(newSegments)
      }
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '')
    const newSegments = [...segments]

    let currentPos = 0
    for (let i = 0; i < 6; i++) {
      const len = lengths[i]
      let segmentVal = pastedData.substring(currentPos, currentPos + len)
      if (i === 0 && segmentVal === '00') {
        segmentVal = '01'
      }
      newSegments[i] = segmentVal
      currentPos += len
    }

    setSegments(newSegments)
    notifyChange(newSegments)
  }

  return (
    <div className='segmented-input-container' onPaste={handlePaste}>
      {lengths.map((len, index) => (
        <React.Fragment key={index}>
          <input
            ref={(el) => (inputRefs.current[index] = el!)}
            type='text'
            className={`segment-box box-${index + 1}`}
            value={segments[index]}
            maxLength={len}
            placeholder={'0'.repeat(len)}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onBlur={() => handleBlur(index)}
          />
        </React.Fragment>
      ))}
    </div>
  )
}

export default SegmentedInput
